/** Public copy adapted from reports/mobile-policy-drafts. Keep reviewed legal copy in English.
 * Every heading, paragraph and action is registered in the normal i18n audit. */
type PolicyLink = { label: string, href: string }
type PolicySection = {
  id: string
  title: string
  paragraphs?: string[]
  items?: string[]
  ordered?: boolean
  links?: PolicyLink[]
}
type PolicyDocument = { title: string, introduction: string, sections: PolicySection[] }

export const POLICY_DOCUMENTS = {
  privacy: {
    title: 'Privacy Policy',
    introduction: 'Elemental Dispatch is developed by Javier Pombo. This policy explains how the game handles your information. For privacy questions, contact admin@elementaldispatch.com.',
    sections: [
      {
        id: 'accounts', title: 'Accounts and saves',
        paragraphs: [
          'You can play without signing in with Google. The game creates an anonymous Firebase account when it needs an account for your Stardust wallet. This account has a unique user ID, so anonymous play still creates account data. Firebase, a Google service, stores accounts and game saves for us.',
          'If you sign in with Google, Firebase Authentication keeps your email address, display name and profile photo URL. Our game server uses your user ID and sign-in provider to identify your account.',
          'We store your current run, unlocked progress, collection, achievements, favorites, puzzle results and game statistics. We also store your Stardust balance, earned and spent currency, owned creatures, Starweave choices and transaction history.',
          'Run records, reward claims, account-transfer records and reset locks help keep progress and rewards consistent. Cloud saves include a game-generated device label to help resolve save conflicts. That label is not an operating-system advertising ID.',
        ],
      },
      {
        id: 'leaderboards', title: 'Public leaderboards and aliases',
        paragraphs: [
          'Leaderboard entries show your alias, selected title, creature avatar and game results. The public leaderboard response also contains your Firebase user ID. Current leaderboard displays hide your Google name and photo. Older leaderboard records may still contain a Google name and photo URL in our database. Choose an alias that does not reveal private information.',
          'Aliases are the only text players can write for other players to see. The game filters aliases and lets signed-in players report them. There is no chat or player messaging. Reports store the reporter’s ID, the reported player’s ID, the alias, the board, a count and a timestamp. We use profile restrictions, reports and ban records to moderate the game.',
          'You can hide another player’s leaderboard details on this device. This preference is stored locally and does not report the player or hide them for other people.',
        ],
      },
      {
        id: 'purchases', title: 'Purchases',
        paragraphs: [
          'When a purchase is processed, the game records the account, currency credited, amount paid, transaction reference and time. Refund and dispute events are recorded for review.',
          'The web checkout uses Stripe and sends it your user ID, pack choice and account-reset version. Stripe collects card and billing details on its own checkout page. Our game does not receive your full payment card details through that checkout. The web purchase shelf is hidden in native app builds.',
        ],
      },
      {
        id: 'security', title: 'Usage counts, errors and security',
        paragraphs: [
          'We count steps in the game to understand where players get stuck. These counts are grouped by day, game version and device class, such as phone or tablet. They do not store account IDs, device IDs, IP addresses, user agents, cookies or individual event times. Error reports use short, scrubbed messages and daily counts to help us fix faults. They do not include stack traces.',
          'Separately, we process IP addresses in memory to limit abusive traffic. IP addresses can also appear in Google Cloud infrastructure logs and a server-start diagnostic sample. Some server logs contain account IDs, aliases or account-related record paths.',
          'Google Firebase App Check and reCAPTCHA Enterprise help check requests and protect the service. The service uses HTTPS and Firebase’s encrypted connections for data in transit.',
        ],
      },
      {
        id: 'device', title: 'Storage on your device',
        paragraphs: [
          'The game keeps local saves, a wallet cache, collection and progress, settings, tutorial choices, hidden-player preferences and a device label on your device. It also keeps sign-in state, sync records and a list of usage steps already counted. Firebase stores sign-in session information locally.',
          'Game code does not set cookies, but Firebase and reCAPTCHA have their own storage behavior.',
        ],
      },
      {
        id: 'services', title: 'Services and advertising',
        paragraphs: [
          'Google provides Firebase Authentication, Firestore, App Check, reCAPTCHA Enterprise and cloud hosting. Stripe provides the web payment service. See their privacy information for their practices.',
          'Cloudflare delivers creature, boss and enemy artwork from art.elementaldispatch.com. Loading hosted artwork sends connection information, including your IP address, to that service.',
          'There are no ads or advertising trackers in Elemental Dispatch today. We do not track you across other companies’ apps or websites for advertising. We do not sell your data or share it for advertising.',
          'We may add advertising in a future version. Before any ads appear, we will update this policy and name each advertising partner. The update will explain the data each partner receives, why it receives it, and your choices. We will obtain any consent or permission the law or platform requires before that new processing starts.',
        ],
        links: [
          { label: 'Google’s privacy policy', href: 'https://policies.google.com/privacy' },
          { label: 'Firebase’s privacy information', href: 'https://firebase.google.com/support/privacy' },
          { label: 'Stripe’s privacy policy', href: 'https://stripe.com/privacy' },
        ],
      },
      {
        id: 'retention', title: 'How long we keep data',
        paragraphs: [
          'The automatic cleanup job targets anonymous accounts with no purchases after 90 days of inactivity. Anonymous accounts with purchases are excluded from that automatic cleanup. Cleanup runs in batches, so 90 days is not a guaranteed deletion deadline. You can use Delete account for either type of account. Signed-in accounts are kept until you delete them.',
          'Account deletion removes your account and the linked data listed on the deletion page from the game’s active systems. We retain a minimal deletion marker indefinitely, keyed to the deleted account ID, so old reward requests and delayed payment events cannot recreate the wallet or other protected records. This marker does not retain your profile, email address, saves, balance or transaction history.',
          'Transfer-code and run-start records have expiry settings. Starweave receipts also carry an expiry field. These settings do not guarantee when every stored copy disappears. Backups, infrastructure logs and independent service-provider records may remain after active account data is deleted; we do not promise a fixed removal date for those records.',
          'Anonymous daily usage and error totals have no configured retention limit. These totals have no account identifier that lets us remove one player’s contribution. Stripe holds separate payment records, and deleting a game account does not itself delete those records.',
        ],
        links: [{ label: 'Account deletion and retained records', href: '/delete-account' }],
      },
      {
        id: 'choices', title: 'Your choices and requests',
        paragraphs: [
          'You can delete your account inside the game or request deletion by email. Signing out or resetting progress is not account deletion.',
          'Contact support to request access, correction or deletion of account data. We use request details to identify the account and verify that you control it. Do not send passwords, sign-in codes or full payment card details.',
          'If you contact support by email, we receive the email address, message and any attachments you send. We use that information to respond to your request and, when needed, verify account ownership.',
          'You may have further privacy rights under the law where you live. Contact support about privacy concerns, including concerns about a child’s information.',
        ],
        links: [
          { label: 'Delete your account', href: '/delete-account' },
          { label: 'Contact privacy support', href: 'mailto:admin@elementaldispatch.com' },
        ],
      },
    ],
  },
  terms: {
    title: 'Terms of Service',
    introduction: 'Elemental Dispatch is developed by Javier Pombo. These terms cover the game and elementaldispatch.com. Contact admin@elementaldispatch.com with questions.',
    sections: [
      {
        id: 'using', title: 'Using the game',
        paragraphs: [
          'By using Elemental Dispatch, you agree to these terms. If you do not agree, do not use the game. You may play the game for personal entertainment. You do not receive ownership of the game, its artwork or its code. Nothing here limits rights that the law gives you.',
          'Keep your sign-in account secure. Do not impersonate another player or a member of the development team. Do not cheat, exploit bugs for unfair rewards, disrupt the service or try to access another player’s account. Report problems to support.',
        ],
      },
      {
        id: 'stardust', title: 'Stardust, purchases and refunds',
        paragraphs: [
          'Stardust is virtual currency for use inside Elemental Dispatch. Stardust and virtual items have no cash value. They cannot be cashed out, sold or traded for money. Stardust never expires.',
          'Only buy through the purchase options offered by the game. Check the price and contents before confirming a purchase. Purchases are final except where the applicable store policy or law provides otherwise. For a refund, use the process offered by the store or payment service that handled the purchase. Contact support if you need help with missing currency or an incorrect balance.',
          'The game keeps a transaction history to credit purchases and review refunds or disputes. Account deletion removes the game wallet, including unused purchased Stardust. Deletion does not itself request a refund. It does not remove any refund right that the store policy or law gives you.',
        ],
      },
      {
        id: 'boosters', title: 'Random booster packs',
        paragraphs: [
          'Booster packs give random creatures. The purchase surface shows the odds before you buy. Buying a pack does not guarantee a particular creature or rarity. Duplicates give the Stardust return shown by the game. Read the displayed odds and duplicate rules before spending Stardust.',
        ],
      },
      {
        id: 'conduct', title: 'Aliases and conduct',
        paragraphs: [
          'Your alias is the name shown on leaderboards. Aliases are the only player-written text visible to other players. There is no chat or player messaging.',
          'Aliases must be 3 to 16 characters long. They may use letters, digits, spaces, periods, underscores and hyphens. Some names are reserved or blocked. You can normally change your alias once every seven days. Choose an alias that follows these rules:',
        ],
        items: [
          'Do not use slurs, threats, harassment, sexual content or offensive language.',
          'Do not impersonate staff or claim official status.',
          'Do not include someone else’s private information.',
          'Do not try to evade the name filter with altered spelling or symbols.',
        ],
      },
      {
        id: 'moderation', title: 'Reports and moderation',
        paragraphs: [
          'The game filters aliases automatically. Signed-in players can report an alias with the report button beside its leaderboard entry. Reports are reviewed manually. Reports include both players’ account IDs and the alias being reported. You can also hide a player’s leaderboard details on your own device.',
          'We may replace a rule-breaking alias with a default name and lock renaming for 30 days. We may restrict or suspend access for serious or repeated abuse. We will consider the severity of the conduct and any relevant explanation. Contact support if you think a moderation decision was a mistake.',
        ],
      },
      {
        id: 'privacy', title: 'Privacy and account deletion',
        paragraphs: [
          'Read the Privacy Policy for the account, save, usage and security information the game handles. The automatic cleanup job targets anonymous accounts with no purchases after 90 days of inactivity; it excludes anonymous accounts with purchases. Signed-in accounts are kept until you delete them.',
          'Use Delete account to remove an anonymous or signed-in account. A minimal permanent deletion marker remains to prevent delayed requests from recreating deleted data. The deletion page explains the process and the records that may remain outside active game data.',
        ],
        links: [
          { label: 'Privacy Policy', href: '/privacy' },
          { label: 'Delete your account', href: '/delete-account' },
        ],
      },
      {
        id: 'changes', title: 'Changes and availability',
        paragraphs: [
          'We may update the game, adjust its balance or pause it for maintenance. We cannot promise that it will always be available or free of errors. We will publish changes to these terms with an updated date and give reasonable notice of material changes where required.',
          'Any store terms that apply to your purchase continue to apply. These terms do not exclude mandatory consumer protections.',
        ],
        links: [{ label: 'Contact support', href: '/support' }],
      },
    ],
  },
  support: {
    title: 'Support',
    introduction: 'Need a hand with Elemental Dispatch? Email admin@elementaldispatch.com. The game is developed by Javier Pombo.',
    sections: [
      {
        id: 'help', title: 'How do I get help?',
        paragraphs: [
          'Describe what happened and what you expected. If useful, include the game version, your phone model, and the steps that caused the problem. Please hide personal details in any screenshot. Never send a password, sign-in code, payment card number, or security code.',
        ],
        links: [{ label: 'Email support', href: 'mailto:admin@elementaldispatch.com' }],
      },
      {
        id: 'signin', title: 'Do I have to sign in?',
        paragraphs: [
          'You can play without signing in with Google. The game creates an anonymous Firebase account when the Stardust wallet needs one. Google sign-in is needed to post leaderboard scores, choose an alias, and report an alias.',
        ],
      },
      {
        id: 'saves', title: 'Where is my progress saved?',
        paragraphs: [
          'The game keeps progress on your device and stores account data and cloud saves with Firebase, a Google service. Saved data includes your run, collection, achievements, favorites, scores, and Stardust wallet.',
          'If progress seems missing, check that you are using the same Google account. Avoid resetting progress or clearing the app while we investigate. Contact support and describe which progress is missing.',
        ],
      },
      {
        id: 'public', title: 'What do other players see?',
        paragraphs: [
          'Leaderboards show your alias, chosen title, creature avatar, and game results. The public leaderboard response also includes your player account ID. Leaderboards do not display your Google name or photo. Older leaderboard records may still contain a Google name or photo in storage.',
        ],
      },
      {
        id: 'aliases', title: 'How do aliases work?',
        paragraphs: [
          'Aliases are the only text that players can write for other players to see. They must be 3–16 characters long. You can use letters, numbers, spaces, periods, underscores, and hyphens. You can normally change your alias once every seven days. The game filters aliases for offensive language and reserved names. Please avoid personal contact details, impersonation, and abusive names.',
        ],
      },
      {
        id: 'report', title: 'How do I report or hide a player?',
        paragraphs: [
          'Sign in with Google and use the report button beside the leaderboard entry. A report records your player ID, the reported player ID, the alias, the board, a count, and a timestamp. Reports are reviewed manually. Moderation can replace an alias with a default name and prevent another change for 30 days. You can contact support about a moderation decision.',
          'Use Hide this player beside a leaderboard row to hide that player’s details on this device. You can show the player again from the hidden row. Hiding is local to this browser or app and does not send a report.',
        ],
      },
      {
        id: 'chat', title: 'Is there chat?',
        paragraphs: ['There is no player chat or private messaging.'],
      },
      {
        id: 'ads', title: 'Does the game have ads or tracking?',
        paragraphs: [
          'There are no ads or advertising trackers today. The game does count usage steps and scrubbed errors in daily totals without account or device IDs. Advertising may be added in a future version. Before any ads appear, we will update the Privacy Policy and name the ad partners.',
        ],
        links: [{ label: 'Read the Privacy Policy', href: '/privacy' }],
      },
      {
        id: 'purchases', title: 'What should I know about Stardust and boosters?',
        paragraphs: [
          'Stardust has no cash value and never expires. Account deletion removes the associated Stardust balance. Purchases are final except where the store’s policy or applicable law provides otherwise. Contact the store or payment service that processed the purchase for a refund request.',
          'Booster packs contain random creatures. The odds are shown where you buy the booster. Read the odds before buying.',
          'For a missing purchase, contact support with the purchase date and store order reference, if available. Omit payment card details.',
        ],
      },
      {
        id: 'retention', title: 'How long is my account kept?',
        paragraphs: [
          'The automatic cleanup job targets anonymous accounts with no purchases after 90 days of inactivity. Accounts with purchases are excluded from that automatic cleanup. Signed-in accounts are kept until you delete them. Use Delete account to remove either an anonymous or a signed-in account.',
        ],
        links: [{ label: 'Data and retention details', href: '/privacy#retention' }],
      },
      {
        id: 'delete', title: 'How do I delete my account?',
        paragraphs: [
          'Open Account from the title menu or Profile, select Delete account, review the warning, and type DELETE to confirm. Google accounts verify ownership through Google first. Anonymous sessions can delete their account without signing in with Google. You can also open the deletion page directly on the web.',
          'Resetting progress is not account deletion. A progress reset keeps the account, alias, leaderboard entries, and reports. Follow the account-deletion instructions for the full process or to request deletion by email.',
        ],
        links: [{ label: 'Delete your account', href: '/delete-account' }],
      },
    ],
  },
  'delete-account': {
    title: 'Delete your account',
    introduction: 'Delete your Elemental Dispatch account and its game data here, including an anonymous account on this device. Elemental Dispatch is developed by Javier Pombo. For help, email admin@elementaldispatch.com.',
    sections: [
      {
        id: 'steps', title: 'Delete inside the app',
        paragraphs: [
          'Open Account from the title menu or Profile and select Delete account, or open this page directly. Both signed-in and anonymous players can use the deletion form on this page.',
          'Deletion is permanent. It removes your account, progress, collection, and Stardust balance, including unused purchased Stardust. Stardust does not expire while held in an existing account, but deleting the account removes its balance. Deletion does not itself request a refund. Refunds follow the store’s policy and applicable law.',
        ],
        ordered: true,
        items: [
          'Check that the account shown in the form is the one you want to delete.',
          'Read the list of data that will be removed.',
          'Type DELETE in the confirmation field. Google accounts must verify ownership through Google; anonymous sessions do not need to sign in with Google.',
          'Choose Delete my account and wait for confirmation that deletion is complete.',
        ],
      },
      {
        id: 'web', title: 'Request deletion by email',
        paragraphs: [
          'You can request deletion without reinstalling the app. Email admin@elementaldispatch.com with the subject Elemental Dispatch account deletion. Tell us that you want your game account and its associated data deleted, whether you used Google or anonymous play, and your alias or player ID if known.',
          'If you used Google sign-in, send the request from that account’s email address when possible. If you cannot access it, explain that in your message. If you played anonymously, say so. Keep the original app session available if you still have it.',
          'We will ask only for what is needed to confirm account ownership. An alias or player ID alone is not proof of ownership. Never send passwords, sign-in codes, access tokens, full payment card numbers, or security codes. We will tell you the expected completion time after verifying the request and confirm when it is complete.',
        ],
        links: [{ label: 'Request account deletion by email', href: 'mailto:admin@elementaldispatch.com?subject=Elemental%20Dispatch%20account%20deletion' }],
      },
      {
        id: 'scope', title: 'What will be deleted?',
        paragraphs: [
          'Account deletion removes the following data from the game’s active systems:',
        ],
        items: [
          'Your Firebase account and its stored sign-in profile.',
          'Your cloud saves, runs, progress, collection, achievements, favorites, and puzzle records.',
          'Your Stardust wallet, balance, transaction history, creature ownership, Starweave records, and request receipts.',
          'Your alias, alias reservation, chosen title, creature avatar, and moderation settings.',
          'Your entries on all leaderboards, including weekly and old daily entries.',
          'Your run-start records, bonus claims, daily claims, wallet-transfer records, reset locks, and account bans.',
          'Reports that identify you as the reporter or the reported player.',
        ],
      },
      {
        id: 'device', title: 'Your session and this device',
        paragraphs: [
          'The app deletion flow signs you out and clears its local account, save, wallet, and progress records on this device. Existing refresh tokens are revoked when the account is deleted.',
          'A web request cannot itself clear local files on a device you no longer use. After confirmation, remove the app’s local data from any other device or browser you used. Deleting the game account does not delete your Google account or store account.',
        ],
      },
      {
        id: 'retained', title: 'What may remain?',
        paragraphs: [
          'A minimal deletion marker, keyed to the deleted account ID, is retained indefinitely. It prevents old reward requests and delayed payment events from recreating deleted account data. It does not retain your profile, email address, saves, balance or transaction history.',
          'Backups and infrastructure logs are separate from active game data and may remain after deletion. Logs can contain IP addresses and player IDs. We do not promise a fixed removal deadline for backups, logs or independent service-provider records.',
          'Daily usage and error totals contain no account identifiers. Those totals cannot be removed for one particular account. Stripe keeps payment records in its own systems; deleting a game account does not delete those records.',
        ],
        links: [{ label: 'Privacy Policy', href: '/privacy' }],
      },
      {
        id: 'reset', title: 'Automatic cleanup and progress reset',
        paragraphs: [
          'The automatic cleanup job targets anonymous accounts with no purchases after 90 days of inactivity. It excludes anonymous accounts with purchases and runs in batches. Signed-in accounts are kept until you delete them. Use this deletion flow for either type of account instead of waiting for automatic cleanup.',
          'Resetting progress does not delete your account. A reset leaves the account, alias, leaderboard records, and reports in place. Use account deletion to remove the account and its linked game data.',
        ],
      },
    ],
  },
} satisfies Record<string, PolicyDocument>

export type PolicyDocumentId = keyof typeof POLICY_DOCUMENTS

export const POLICY_MESSAGES: Record<string, string> = {
  'policy.privacy': 'Privacy',
  'policy.terms': 'Terms',
  'policy.support': 'Support',
  'policy.deleteAccount': 'Delete account',
  'policy.navigation': 'Policies and support',
  'policy.backToGame': 'Back to the game',
  'policy.updated': 'Last updated: 13 October 2026',
  'policy.language': 'This page is available in English.',
  'policy.brand': 'Elemental Dispatch',
}

for (const [page, document] of Object.entries(POLICY_DOCUMENTS)) {
  POLICY_MESSAGES[`policy.${page}.title`] = document.title
  POLICY_MESSAGES[`policy.${page}.introduction`] = document.introduction
  for (const section of document.sections as PolicySection[]) {
    const prefix = `policy.${page}.${section.id}`
    POLICY_MESSAGES[`${prefix}.title`] = section.title
    section.paragraphs?.forEach((paragraph, index) => { POLICY_MESSAGES[`${prefix}.paragraph.${index}`] = paragraph })
    section.items?.forEach((item, index) => { POLICY_MESSAGES[`${prefix}.item.${index}`] = item })
    section.links?.forEach((link, index) => { POLICY_MESSAGES[`${prefix}.link.${index}`] = link.label })
  }
}

export function policyDocument(id: PolicyDocumentId): PolicyDocument { return POLICY_DOCUMENTS[id] }
